import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { AccordionNodeView } from './accordion-node-view'
import { nanoid } from 'nanoid'
import { IconCategory, IconType } from '@deepguide-ai/types'

export interface AccordionAttributes {
  id?: string
  title?: string
  description?: string
  defaultOpen?: boolean
  icon?: string
  iconType?: IconType
  iconCategory?: IconCategory
  iconColor?: string
  showIcon?: boolean
}

export const AccordionSpec = {
  group: 'block',
  content: 'block*',
  attrs: {
    id: { default: nanoid(10) },
    title: { default: '' },
    description: { default: '' },
    defaultOpen: { default: false },
    icon: { default: '' },
    iconType: { default: 'REGULAR' },
    iconCategory: { default: 'LUCIDE'},
    iconColor: { default: '#3b82f6' },
    showIcon: { default: false },
  },
  toDOM() { return ['div', { 'data-accordion': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'div[data-accordion]' }],
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    accordion: {
      /**
       * Insert an accordion
       */
      insertAccordion: (attributes?: AccordionAttributes) => ReturnType
      /**
       * Update accordion attributes
       */
      updateAccordion: (attributes: Partial<AccordionAttributes>) => ReturnType
    }
  }
}

export const AccordionExtension = Node.create({
  name: 'Accordion',

  group: 'block',

  content: 'block+',

  isolating: true,

  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-dg-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-dg-id': attributes.id,
          }
        },
      },
      title: {
        default: 'Accordion',
        parseHTML: (element) => element.getAttribute('data-title'),
        renderHTML: (attributes) => {
          if (!attributes.title) {
            return {}
          }
          return {
            'data-title': attributes.title,
          }
        },
      },
      description: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-description'),
        renderHTML: (attributes) => {
          if (!attributes.description) {
            return {}
          }
          return {
            'data-description': attributes.description,
          }
        },
      },
      defaultOpen: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute('data-default-open') === 'true',
        renderHTML: (attributes) => {
          if (attributes.defaultOpen === undefined) {
            return {}
          }
          return {
            'data-default-open': attributes.defaultOpen,
          }
        },
      },
      icon: {
        default: 'sparkles',
        parseHTML: (element) => element.getAttribute('data-icon'),
        renderHTML: (attributes) => {
          if (!attributes.icon) {
            return {}
          }
          return {
            'data-icon': attributes.icon,
          }
        },
      },
      iconCategory: {
        default: 'LUCIDE',
        parseHTML: (element) => element.getAttribute('data-icon-category'),
        renderHTML: (attributes) => {
          if (!attributes.iconCategory) {
            return {}
          }
          return {
            'data-icon-category': attributes.iconCategory,
          }
        },
      },
      iconType: {
        default: 'REGULAR',
        parseHTML: (element) => element.getAttribute('data-icon-type'),
        renderHTML: (attributes) => {
          if (!attributes.iconType) {
            return {}
          }
          return {
            'data-icon-type': attributes.iconType,
          }
        },
      },
      iconColor: {
        default: '#3b82f6',
        parseHTML: (element) => element.getAttribute('data-icon-color'),
        renderHTML: (attributes) => {
          if (!attributes.iconColor) {
            return {}
          }
          return {
            'data-icon-color': attributes.iconColor,
          }
        },
      },
      showIcon: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute('data-show-icon') === 'true',
        renderHTML: (attributes) => {
          if (attributes.showIcon === undefined) {
            return {}
          }
          return {
            'data-show-icon': attributes.showIcon,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="Accordion"]',
      },
      {
        tag: 'div.accordion-item',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'Accordion',
        class:
          'accordion-item group/item border-border relative my-5 overflow-hidden rounded-md border border-solid p-0',
      }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(AccordionNodeView)
  },

  addCommands() {
    return {
      insertAccordion:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              title: 'Accordion',
              description: '',
              defaultOpen: false,
              icon: 'sparkles',
              iconCategory: 'LUCIDE',
              iconType: 'REGULAR',
              iconColor: '#3b82f6',
              showIcon: false,
              ...attributes,
            },
            content: [
              {
                type: 'paragraph',
                content: [
                  
                ]
              }
            ],
          })
        },
      updateAccordion:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Prevent deleting the accordion with backspace when cursor is at the beginning
      Backspace: ({ editor }) => {
        const { selection, doc } = editor.state
        const { $from } = selection

        // Check if we're at the beginning of an accordion
        if ($from.pos === 1) return false

        const node = $from.node()
        if (node.type.name === this.name && $from.parentOffset === 0) {
          return true // Prevent deletion
        }

        return false
      },
    }
  },
})
